import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ReportsService } from '../../../services/reports.service';

export interface CrmTarget {
	debtorno?: string;
	branchcode?: string;
	prospect_key?: string;
	name: string;
}

interface CrmNote {
	id: number;
	note_type: 'call' | 'visit' | 'follow_up' | 'general';
	body: string;
	note_date: string;
	due_date: string | null;
	completed_at: string | null;
	salesperson: string;
	salesmanname?: string;
	created_at: string;
	has_geo?: number;
}

@Component({
	selector: 'app-crm-notes-panel',
	templateUrl: './crm-notes-panel.component.html',
	styleUrls: ['./crm-notes-panel.component.scss']
})
export class CrmNotesPanelComponent implements OnChanges {
	@Input() target: CrmTarget | null = null;
	@Input() salesperson = '';
	@Output() closed = new EventEmitter<void>();
	@Output() noteAdded = new EventEmitter<void>();

	loading = false;
	saving = false;
	notes: CrmNote[] = [];
	openFollowups = 0;
	status: string | null = null;

	newType: 'general' | 'call' | 'follow_up' = 'general';
	newBody = '';
	newDue = '';

	readonly typeIcons: Record<string, string> = {
		call: 'call', visit: 'how_to_reg', follow_up: 'flag', general: 'sticky_note_2'
	};
	readonly typeLabels: Record<string, string> = {
		call: 'Call', visit: 'Visit', follow_up: 'Follow-up', general: 'Note'
	};

	constructor(private reportsService: ReportsService, private cdr: ChangeDetectorRef) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['target'] && this.target) {
			this.notes = [];
			this.status = null;
			this.newBody = '';
			this.newDue = '';
			this.newType = 'general';
			this.load();
		}
	}

	close(): void { this.closed.emit(); }

	load(): void {
		if (!this.target) { return; }
		this.loading = true;
		this.reportsService.getSalesRouteNotes(this.target).subscribe({
			next: (res: any) => {
				this.loading = false;
				this.notes = res?.success ? (res.notes || []) : [];
				this.openFollowups = Number(res?.open_followups || 0);
				this.cdr.markForCheck();
			},
			error: () => { this.loading = false; this.status = 'Could not load notes.'; this.cdr.markForCheck(); }
		});
	}

	addNote(): void {
		if (!this.target || !this.newBody.trim()) { return; }
		this.saving = true;
		this.status = null;
		this.reportsService.postSalesRouteNoteSave({
			debtorno: this.target.debtorno || '',
			branchcode: this.target.branchcode || '',
			prospect_key: this.target.prospect_key || '',
			note_type: this.newType,
			body: this.newBody.trim(),
			due_date: this.newType === 'follow_up' ? this.newDue : '',
			salesperson: this.salesperson,
		}).subscribe({
			next: (res: any) => {
				this.saving = false;
				if (res?.success) {
					this.newBody = '';
					this.newDue = '';
					this.noteAdded.emit();
					this.load();
				} else {
					this.status = res?.message || 'Could not save the note.';
				}
				this.cdr.markForCheck();
			},
			error: () => { this.saving = false; this.status = 'Could not save the note.'; this.cdr.markForCheck(); }
		});
	}

	completeFollowup(n: CrmNote): void {
		this.reportsService.postSalesRouteFollowupComplete({ note_id: n.id }).subscribe({
			next: (res: any) => { if (res?.success) { this.load(); } },
			error: () => { this.status = 'Could not complete the follow-up.'; this.cdr.markForCheck(); }
		});
	}

	isOpenFollowup(n: CrmNote): boolean {
		return n.note_type === 'follow_up' && !n.completed_at;
	}

	isOverdue(n: CrmNote): boolean {
		if (!this.isOpenFollowup(n) || !n.due_date) { return false; }
		return n.due_date < new Date().toISOString().slice(0, 10);
	}
}
